import React, { useEffect, useState } from "react";
import {
  Box,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  SimpleGrid,
  Switch,
  Flex,
  Text,
} from "@chakra-ui/react";
import { PERMISSION_PRESETS } from "./permissionPresets";
import PermissionToggleBox from "./PermissionToggleBox";
import { textPrimary, textSecondary, white } from "../../../../../utils/colors";
import { useTranslation } from "react-i18next";

const PermissionSelector = ({ role, setRole, permissions, setPermissions }) => {
  const { t } = useTranslation();
  const [internalPerms, setInternalPerms] = useState(permissions || {});
  const [isCustom, setIsCustom] = useState(false);
  const [openIndexes, setOpenIndexes] = useState([]); // Track which accordion sections are open

  useEffect(() => {
    if (role !== "custom") {
      const newPerms = PERMISSION_PRESETS[role];
      setInternalPerms(newPerms);
      setPermissions(newPerms);
      setIsCustom(false);
      setOpenIndexes([]); // Reset expanded accordions on role change
    }
  }, [role]);

  const handlePermChange = (section, field) => {
    const currentSection = internalPerms?.[section] || {};

    const updatedSection = {
      ...currentSection,
      [field]: !currentSection?.[field],
      on: currentSection.on || !currentSection?.[field], // turn 'on' if it was off and user is enabling a permission
    };

    const updated = {
      ...internalPerms,
      [section]: updatedSection,
    };

    setInternalPerms(updated);
    setPermissions(updated);
    setRole("custom");
    setIsCustom(true);

    // Auto-open accordion if turning on
    if (!currentSection.on && !currentSection?.[field]) {
      setOpenIndexes((prev) => [...new Set([...prev, section])]);
    }
  };

  const handleSectionToggle = (section, index) => {
    const current = internalPerms?.[section]?.on || false;

    const updatedSection = {
      ...internalPerms[section],
      on: !current,
    };

    if (current) {
      // If turning OFF: set all permissions in the group to false
      Object.keys(PERMISSION_PRESETS.admin[section]).forEach((key) => {
        if (key !== "on") updatedSection[key] = false;
      });
    }

    const updated = {
      ...internalPerms,
      [section]: updatedSection,
    };

    setInternalPerms(updated);
    setPermissions(updated);
    setRole("custom");
    setIsCustom(true);

    // Accordion logic
    if (!current) {
      setOpenIndexes((prev) => [...new Set([...prev, index])]);
    } else {
      setOpenIndexes((prev) => prev.filter((i) => i !== index));
    }
  };

  // console.log("Internal Permissions:", internalPerms);

  return (
    <Box w="100%">
      <Accordion
        allowMultiple
        w="100%"
        index={openIndexes}
        onChange={setOpenIndexes}
      >
        {Object.entries(PERMISSION_PRESETS.admin).map(
          ([sectionKey, sectionPerms], idx) => {
            const subPermissionKeys = Object.keys(sectionPerms).filter(
              (key) => key !== "on"
            );
            const isExpandable = subPermissionKeys.length > 0;

            return (
              <AccordionItem
                key={sectionKey}
                bg={white}
                borderRadius="1rem"
                mb={3}
                border="none"
              >
                <AccordionButton
                  _expanded={{ bg: white }}
                  p="1rem 1.5rem"
                  borderRadius="1rem"
                  role="group"
                  _hover={{
                    borderRadius: "1rem",
                    boxShadow: "1px 1px 1px 6px rgba(0, 0, 0, 0.01)",
                  }}
                  cursor={isExpandable ? "pointer" : "default"}
                  onClick={(e) => {
                    if (!isExpandable) {
                      // prevent accordion toggle and only trigger section toggle
                      e.preventDefault();
                      handleSectionToggle(sectionKey, idx);
                    }
                  }}
                >
                  <Flex flex="1" justify="space-between" align="center">
                    <Flex align="center">
                      <Switch
                        size="sm"
                        isChecked={internalPerms?.[sectionKey]?.on || false}
                        onChange={() => handleSectionToggle(sectionKey, idx)}
                        mr={2}
                      />
                      <Text
                        fontSize="sm"
                        color={
                          internalPerms?.[sectionKey]?.on
                            ? textPrimary
                            : textSecondary
                        }
                        fontWeight="500"
                        _groupHover={{ pl: "0.25rem" }}
                        transition="padding-left 0.2s ease-in-out"
                      >
                        {t(`permission.${sectionKey}`)}
                      </Text>
                    </Flex>
                  </Flex>

                  {isExpandable && <AccordionIcon />}
                </AccordionButton>

                {isExpandable && (
                  <AccordionPanel p="0.75rem 1.5rem 1.75rem">
                    <SimpleGrid columns={{ base: 2, md: 2 }} spacing={0} p="0">
                      {subPermissionKeys.map((permKey) => (
                        <PermissionToggleBox
                          key={permKey}
                          label={permKey}
                          section={sectionKey}
                          isChecked={
                            internalPerms?.[sectionKey]?.[permKey] || false
                          }
                          onToggle={() => handlePermChange(sectionKey, permKey)}
                          isDisabled={!internalPerms?.[sectionKey]?.on}
                        />
                      ))}
                    </SimpleGrid>
                  </AccordionPanel>
                )}
              </AccordionItem>
            );
          }
        )}
      </Accordion>
    </Box>
  );
};

export default PermissionSelector;
